// src/components/common/QuickThemeSwitcher.js
import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPalette, FaCheck, FaChevronDown } from 'react-icons/fa';
import { ThemeContext } from '../../context/ThemeContext';
import { getAllThemeIds, getThemeById } from '../../styles/colorThemes';
// Theme colors now use CSS variables from index.css

const SwitcherContainer = styled.div`
  position: relative;
  z-index: 3;
`;

const SwitcherButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  position: relative;
  flex-shrink: 0;
  white-space: nowrap;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: var(--primary-color);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    transform: translateY(-2px);
    color: var(--primary-color);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg:last-child {
    font-size: 0.75rem;
    transition: transform 0.3s ease;
  }
  
  &.open svg:last-child {
    transform: rotate(180deg);
  }
  
  /* Tablet */
  @media (max-width: 1024px) {
    padding: 0.45rem 0.65rem;
    font-size: 1rem;
    gap: 0.4rem;
  }
  
  /* Mobile - hide text, show only icon */
  @media (max-width: 768px) {
    padding: 0.4rem;
    gap: 0;
    
    span {
      display: none;
    }
    
    svg:last-child {
      display: none;
    }
  }
`;

const ThemeDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: rgba(15, 15, 15, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  min-width: 280px;
  max-width: 320px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  padding: 0.75rem;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--primary-color);
    border-radius: 3px;
    
    &:hover {
      background: var(--primary-light);
    }
  }
`;

const ThemeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ThemeItem = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: ${props => props.selected 
    ? 'var(--primary-rgba-20)'
    : 'transparent'};
  border: 1px solid ${props => props.selected 
    ? 'var(--primary-color)'
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${props => props.selected 
    ? 'var(--primary-color)'
    : 'var(--text-primary-dark)'};
  font-weight: ${props => props.selected ? '600' : '400'};
  
  &:hover {
    background: ${props => props.selected 
      ? 'var(--primary-rgba-25)'
      : 'rgba(255, 255, 255, 0.1)'};
    border-color: var(--primary-color);
    transform: translateX(4px);
    box-shadow: ${props => props.selected
      ? '0 0 15px var(--primary-rgba-30)'
      : 'none'};
  }
  
  &:active {
    transform: translateX(2px);
  }
`;

const ThemePreview = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  flex: 1;
`;

const ColorSwatch = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.color};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const ThemeName = styled.span`
  font-weight: 500;
  font-size: 0.9rem;
  margin-left: 0.5rem;
`;

const CheckIcon = styled(motion.div)`
  color: white;
  font-size: 0.875rem;
`;

const QuickThemeSwitcher = () => {
  const { selectedColorTheme, setSelectedColorTheme } = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const allThemeIds = getAllThemeIds();
  const mode = 'dark'; // Always dark mode
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  const handleThemeSelect = (themeId) => {
    setSelectedColorTheme(themeId);
    localStorage.setItem('selectedColorTheme', themeId);
    setIsOpen(false);
  };
  
  const currentTheme = getThemeById(selectedColorTheme, mode) || getThemeById('royalPurple', mode);
  
  return (
    <SwitcherContainer ref={dropdownRef}>
      <SwitcherButton
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title="Quick Theme Switcher"
      >
        <FaPalette />
        <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
          {currentTheme?.name || 'Theme'}
        </span>
        <FaChevronDown />
      </SwitcherButton>
      
      <AnimatePresence>
        {isOpen && (
          <ThemeDropdown
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <ThemeList>
              {allThemeIds.map((themeId) => {
                const colorTheme = getThemeById(themeId, mode) || getThemeById('royalPurple', mode);
                if (!colorTheme) return null;
                
                const isSelected = selectedColorTheme === themeId;
                
                return (
                  <ThemeItem
                    key={themeId}
                    selected={isSelected}
                    onClick={() => handleThemeSelect(themeId)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ThemePreview>
                      {(colorTheme.preview || []).slice(0, 4).map((color, index) => (
                        <ColorSwatch key={index} color={color} />
                      ))}
                      <ThemeName>{colorTheme?.name || themeId}</ThemeName>
                    </ThemePreview>
                    {isSelected && (
                      <CheckIcon
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      >
                        <FaCheck />
                      </CheckIcon>
                    )}
                  </ThemeItem>
                );
              })}
            </ThemeList>
          </ThemeDropdown>
        )}
      </AnimatePresence>
    </SwitcherContainer>
  );
};

export default QuickThemeSwitcher;

